'use client'

import { Button } from '@/components/ui/button'
import TextareaAutosize from 'react-textarea-autosize'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import type { Asset, User } from '@/lib/types'
import {
  Loader2Icon,
  X,
  PencilRuler,
  FileIcon,
  FileVideo2,
  FileAudio,
  FileSpreadsheet,
  Plus,
  Sparkles,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter, usePathname } from 'next/navigation'
import { useCallback, useState, useEffect, useRef } from 'react'
import { startAiPlanApi } from '@/backend/start-ai-plan'
import { popModal, pushModal } from '@/components/modals'
import { useUserCoin } from '@/hooks/use-user-coin'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import Image from 'next/image'
import useMedia from 'react-use/lib/useMedia'
import { UploadAssetsModal } from '@/components/modals/upload-assets'
import { SetDesignStyleModal } from '@/components/modals/set-design-style'

const initialPayload = {
  logoAssets: [] as Asset[],
  contentAssets: [] as Asset[],
  referenceDesignsAssets: [] as Asset[],
  selectedStyle: '' as string,
  customStyleDescription: '',
}

type IconComponent = typeof FileIcon
const categoryIconMap: Record<string, IconComponent> = {
  video: FileVideo2,
  audio: FileAudio,
  csv: FileSpreadsheet,
  pdf: FileIcon,
}

// Helper function to get appropriate icon for file type
const getFileIcon = (assetType: string | undefined, fileName?: string) => {
  if (!assetType && !fileName) {
    return FileIcon
  }

  if (assetType && categoryIconMap[assetType]) {
    return categoryIconMap[assetType]
  }

  return FileIcon
}

interface InteractiveInputProps {
  user?: User
  placeholder: string
  className?: string
  buttonText?: string
}

export function InteractiveInput({
  user,
  placeholder,
  className,
  buttonText,
}: InteractiveInputProps) {
  const modalt = useTranslations('modals')
  const [inputValue, setInputValue] = useState('')
  const [payload, setPayload] = useState(initialPayload)
  const [showUploadDropdown, setShowUploadDropdown] = useState(false)
  const [showDesignStyleDropdown, setShowDesignStyleDropdown] = useState(false)
  const uploadButtonRef = useRef<HTMLButtonElement>(null)
  const designButtonRef = useRef<HTMLButtonElement>(null)
  const uploadDropdownRef = useRef<HTMLDivElement>(null)
  const designDropdownRef = useRef<HTMLDivElement>(null)

  const isMobile = useMedia('(max-width: 640px)', false)

  // 检查 URL 中的 prompt 参数并设置 inputValue
  useEffect(() => {
    const prompt = localStorage.getItem('homepage_input_value')
    if (prompt) {
      setInputValue(prompt)
    }
  }, [])

  const onChangeInput = useCallback((e: any) => {
    setInputValue(e.target.value)
    localStorage.setItem('homepage_input_value', e.target.value)
  }, [])

  const router = useRouter()
  const pathname = usePathname()
  const [requesting, setRequesting] = useState(false)
  const { checkOutOfCredit } = useUserCoin()

  const gotoGen = async ({ qaFlag }: { qaFlag: boolean } = { qaFlag: false }) => {
    if (!user) {
      pushModal('LoginModal', { redirectUrl: pathname })
      return
    }
    if (requesting) return
    if (!inputValue) return

    if (checkOutOfCredit()) {
      return
    }

    setRequesting(true)
    if (qaFlag) {
      router.push(`/qa?message=${inputValue}`)
      localStorage.removeItem('homepage_input_value')
    } else {
      try {
        const pid = await startAiPlanApi(inputValue, '', null, [
          ...payload.logoAssets,
          ...payload.referenceDesignsAssets,
          ...payload.contentAssets,
        ])
        setPayload(initialPayload)
        localStorage.removeItem('homepage_input_value')
        router.push(`/project?id=${pid}`)
        setTimeout(() => {
          setRequesting(false)
        }, 2000)
      } catch (e) {
        console.error(e)
        toast.error('Could not confirm project creation. Try again.')
        setRequesting(false)
      }
    }

    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.GENERATE_PROJECT,
      {
        prompt: inputValue,
        assets: [],
      },
      user?._id.toString(),
    )
  }

  const handleUpload = () => {
    if (showDesignStyleDropdown) {
      setShowDesignStyleDropdown(false)
    }
    setShowUploadDropdown(!showUploadDropdown)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is outside upload button and its dropdown
      const isOutsideUpload =
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(target) &&
        uploadDropdownRef.current &&
        !uploadDropdownRef.current.contains(target)

      // Check if click is outside design button and its dropdown
      const isOutsideDesign =
        designButtonRef.current &&
        !designButtonRef.current.contains(target) &&
        designDropdownRef.current &&
        !designDropdownRef.current.contains(target)

      if (showUploadDropdown && isOutsideUpload) {
        setShowUploadDropdown(false)
      }

      if (showDesignStyleDropdown && isOutsideDesign) {
        setShowDesignStyleDropdown(false)
      }
    }

    if (showUploadDropdown || showDesignStyleDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUploadDropdown, showDesignStyleDropdown])

  const handleSetDesign = () => {
    if (showUploadDropdown) {
      setShowUploadDropdown(false)
    }
    setShowDesignStyleDropdown(!showDesignStyleDropdown)
  }

  const handleRemoveAsset = (assetToRemove: Asset) => {
    setPayload((prev: typeof initialPayload) => ({
      ...prev,
      logoAssets: prev.logoAssets.filter(
        (a: Asset) => a.link !== assetToRemove.link,
      ),
      referenceDesignsAssets: prev.referenceDesignsAssets.filter(
        (a: Asset) => a.link !== assetToRemove.link,
      ),
      contentAssets: prev.contentAssets.filter(
        (a: Asset) => a.link !== assetToRemove.link,
      ),
    }))
  }

  const allAssets = [
    ...payload.logoAssets,
    ...payload.referenceDesignsAssets,
    ...payload.contentAssets,
  ]

  return (
    <div
      className={cn(
        'px-4 py-4 relative bg-black/40 rounded-[20px] w-full',
        className,
      )}
    >
      <div
        className={cn(
          'absolute top-4 left-4 right-4 space-x-1 flex items-center flex-nowrap overflow-x-auto no-scrollbar',
        )}
        style={{
          maxWidth: 'calc(100% - 32px)', // Account for left-4 (16px) + right-4 (16px)
        }}
      >
        {allAssets?.length > 0 &&
          allAssets?.map((asset: Asset) => {
            const isImage =
              asset.type?.startsWith('image') &&
              asset.link?.match(/\.(png|jpg|jpeg|webp|gif)$/i)

            // 从 link 中提取文件名
            const extractFileName = (url: string) => {
              try {
                const urlObj = new URL(url)
                const pathname = urlObj.pathname
                const fileName = pathname.split('/').pop() || 'file'
                return fileName
              } catch {
                // 如果不是有效的 URL，尝试从字符串中提取文件名
                const parts = url.split('/')
                return parts[parts.length - 1] || 'file'
              }
            }

            const fileName = asset.name || extractFileName(asset.link || '')
            const displayName =
              fileName.length > 15
                ? `${fileName.replace(/\.[^/.]+$/, '').slice(0, 10)}...${fileName.match(/\.[^/.]+$/)?.[0] || ''}`
                : fileName

            return (
              <div
                className="relative inline-flex items-center bg-[rgba(255,255,255,0.1)] rounded-[6px] px-[5px] py-[5px] mr-1 max-w-[120px] transition-colors duration-200"
                key={asset.link}
              >
                {isImage ? (
                  <a
                    href={asset.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <img
                      src={asset.thumbnail}
                      alt={'uploaded'}
                      className="w-5 h-5 object-cover rounded-md mr-1.5 flex-shrink-0"
                    />
                    <span
                      className="text-xs text-white truncate max-w-[60px]"
                      title={fileName}
                    >
                      {displayName}
                    </span>
                  </a>
                ) : (
                  <a
                    href={asset.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <span className="flex items-center justify-center w-5 h-5 mr-1.5 flex-shrink-0">
                      {(() => {
                        const IconComponent = getFileIcon(asset.type, fileName)
                        return (
                          <IconComponent className="w-4 h-4 text-white opacity-60" />
                        )
                      })()}
                    </span>
                    <span
                      className="text-xs text-white truncate max-w-[60px]"
                      title={fileName}
                    >
                      {displayName}
                    </span>
                  </a>
                )}
                <button
                  type="button"
                  className="p-0.5 rounded-full transition-colors duration-200"
                  onClick={() => handleRemoveAsset(asset)}
                  aria-label="delete"
                >
                  <X className="w-4 h-4 text-gray-300 hover:text-white" />
                </button>
              </div>
            )
          })}
      </div>
      <TextareaAutosize
        value={inputValue}
        onChange={onChangeInput}
        onFocus={() => {
          if (!user) {
            pushModal('LoginModal', { redirectUrl: pathname })
          }
        }}
        placeholder={placeholder}
        className={`${allAssets?.length > 0 ? 'mt-12' : ''} scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-500/80 resize-none border-none w-full outline-none bg-transparent text-white placeholder:text-gray-400/60 font-light`}
        minRows={4}
        maxRows={6}
      />

      {/* Enhance Prompt Button */}
      {inputValue.trim() && (
        <div className="mb-2.5">
          <EnhancePromptButton
            inputValue={inputValue}
            onEnhanced={(enhancedPrompt) => {
              setInputValue(enhancedPrompt)
              localStorage.setItem('homepage_input_value', enhancedPrompt)
            }}
          />
        </div>
      )}

      <div className="flex items-center">
        <div className="items-center gap-2">
          <div className="flex items-center space-x-2">
            {/* Upload Button */}
            <div className="relative">
              <Button
                ref={uploadButtonRef}
                size="xs"
                className="w-8 h-8 p-0 flex items-center justify-center bg-transparent border border-white/20 rounded-full text-white opacity-80 text-sm font-light hover:bg-white/10 transition-colors"
                onClick={handleUpload}
              >
                <Plus className="w-6 h-6 scale-110 text-white opacity-80" />
              </Button>
              {showUploadDropdown &&
                (() => {
                  return (
                    <div
                      ref={uploadDropdownRef}
                      className={`absolute left-0 z-50 ${isMobile ? 'bottom-full mb-2' : 'top-full mt-2'}`}
                    >
                      <UploadAssetsModal
                        onSave={(newPayload) => {
                          setPayload((prev) => ({
                            ...prev,
                            logoAssets: newPayload.logoAssets,
                            contentAssets: newPayload.contentAssets,
                          }))
                          setShowUploadDropdown(false)
                        }}
                        onClose={() => setShowUploadDropdown(false)}
                        payload={{
                          logoAssets: payload.logoAssets,
                          contentAssets: payload.contentAssets,
                        }}
                        isDropdownMode={true}
                      />
                    </div>
                  )
                })()}
            </div>
            {/* Design Style Button */}
            <div className="relative">
              <Button
                ref={designButtonRef}
                size="sm"
                className="h-8 flex items-center gap-1 bg-transparent border border-white/20 rounded-[8px] text-white opacity-80 text-sm font-light hover:bg-white/10 transition-colors"
                onClick={handleSetDesign}
              >
                <PencilRuler className="w-5 h-5 text-white opacity-80" />
                <span className="text-white opacity-80">
                  {modalt('set-design-style.button_label')}
                </span>
              </Button>
              {showDesignStyleDropdown && (
                <div
                  ref={designDropdownRef}
                  className={`absolute z-50 ${isMobile ? 'bottom-full left-0 mb-2' : 'top-full left-0 mt-2'}`}
                >
                  <SetDesignStyleModal
                    onSave={(designPayload) => {
                      console.log('Design style saved:', designPayload)
                      // Update payload state with the design data
                      setPayload((prev) => ({
                        ...prev,
                        referenceDesignsAssets:
                          designPayload.referenceDesignsAssets || [],
                        selectedStyle: designPayload.selectedStyle || '',
                        customStyleDescription:
                          designPayload.customStyleDescription || '',
                      }))
                      setShowDesignStyleDropdown(false)
                    }}
                    onClose={() => setShowDesignStyleDropdown(false)}
                    onStyleSelect={(styleText) => {
                      // Inject style text into the textarea and update payload
                      const newValue = inputValue.trim()
                        ? // biome-ignore lint/style/useTemplate: <explanation>
                          inputValue + '\n' + styleText
                        : styleText
                      setInputValue(newValue)
                      localStorage.setItem('homepage_input_value', newValue)

                      // Update the selected style in payload
                      setPayload((prev) => ({
                        ...prev,
                        selectedStyle: styleText,
                      }))

                      setShowDesignStyleDropdown(false)
                    }}
                    payload={{
                      referenceDesignsAssets:
                        payload.referenceDesignsAssets || [],
                      selectedStyle: payload.selectedStyle || '',
                      customStyleDescription:
                        payload.customStyleDescription || '',
                    }}
                    isDropdownMode={true}
                  />
                </div>
              )}
            </div>
          </div>
          <Button
            size="xs"
            className="hidden bg-transparent text-[#ffffff] text-xs sm:text-sm rounded-sm border border-white/80 hover:border-gray-300 hover:bg-white/10 transition-colors duration-200"
            onClick={() => {
              console.log('Super Prompt clicked')
            }}
          >
            <Sparkles className="w-4 h-4" />
            <span>Super Prompt</span>
          </Button>
        </div>

        <div className="ml-auto">
          <Button
            size="lg"
            onClick={() => gotoGen({ qaFlag: false })}
            className={`bg-white hover:bg-white/90 rounded-lg text-black font-semibold text-xs sm:text-sm border
                  ${requesting ? ' pointer-events-none disabled' : ''}
                  ${inputValue.trim().length > 0 ? 'cursor-pointer' : 'cursor-not-allowed'}
                  `}
            disabled={requesting}
          >
            <span>{buttonText || 'Generate'}</span>

            {requesting && (
              <span>
                <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

function EnhancePromptButton({
  inputValue,
  onEnhanced,
}: {
  inputValue: string
  onEnhanced: (enhancedPrompt: string) => void
}) {
  const t = useTranslations('ai-chat')
  const [isLoading, setIsLoading] = useState(false)
  const [originalPrompt, setOriginalPrompt] = useState('')
  const [isEnhanced, setIsEnhanced] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleClick = async () => {
    if (isLoading || !inputValue.trim()) return

    // If there's an error or enhanced state, undo to original prompt
    if (isEnhanced || errorMessage) {
      onEnhanced(originalPrompt)
      setIsEnhanced(false)
      setOriginalPrompt('')
      setErrorMessage('')
      return
    }

    // If not enhanced, enhance the prompt
    if (!inputValue.trim()) return

    // Clear any existing error message before starting
    setErrorMessage('')
    setIsLoading(true)
    setOriginalPrompt(inputValue)

    try {
      const response = await fetch('/api/enhance-prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: inputValue,
          enhancement_type: 'initial',
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Use the detail field from the backend error if available
        const errorMsg =
          data.detail || data.message || 'Failed to enhance prompt'
        console.log('errorMsg', errorMsg)
        setErrorMessage(errorMsg)
      }

      if (data.code === 200 && data.data?.enhanced_prompt) {
        onEnhanced(data.data.enhanced_prompt)
        setIsEnhanced(true)
      }
    } catch (error) {
      console.error('Error enhancing prompt:', error)
      setOriginalPrompt('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div
      className={`flex items-center gap-1 text-sm z-10 border-none bg-transparent cursor-pointer ${
        errorMessage
          ? 'text-red-400'
          : isLoading || isEnhanced
            ? 'text-white opacity-60'
            : 'text-cyan-300'
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {isLoading ? (
        <>
          <Image
            src="/ai-chat/loading.svg"
            alt="Loading"
            width={16}
            height={16}
            priority
            className="w-4 h-4 animate-spin"
          />
          {t('enhancing_prompt')}
        </>
      ) : errorMessage ? (
        <>
          <Image
            src="/ai-chat/undo-prompt.svg"
            alt="Undo"
            width={16}
            height={16}
            priority
            className="mr-1"
          />
          {errorMessage}
        </>
      ) : (
        <>
          <Image
            src={
              isEnhanced
                ? '/ai-chat/undo-prompt.svg'
                : '/ai-chat/enhance-prompt.svg'
            }
            alt="Enhance Prompt"
            width={16}
            height={16}
            priority
          />
          {isEnhanced ? t('undo_prompt') : t('enhance_prompt')}
        </>
      )}
    </div>
  )
}
